import { Fragment, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowUpRight, ChevronRight, X } from 'lucide-react';
import { Card, CardHeader, StatTile } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { EmptyState, ErrorState, LoadingState } from '../../../components/ui/States';
import { Alert } from '../../../components/ui/Alert';
import { Table, TableFooter, TGroupRow, THead, Th, Td, Tr } from '../../../components/ui/Table';
import { useCustomers } from '../../../hooks/useCustomers';
import { money } from './formatters';
import { ReceivablesAging } from './ReceivablesAging';
import { overrideConflictSentence } from './overrideCheck';
import { TileSkeleton } from './TileSkeleton';
import {
  BLOCKED_LABEL,
  PAYMENT_BADGE,
  PHASE_VARIANT,
  STAGE_BADGE,
  UNKNOWN,
  billingNote,
  billingNoteShort,
  contractBadgeLabel,
  customerSinceLabel,
  dayMonth,
  invoiceCounts,
  insightBadge,
  latestInsight,
  endedStillInMrr,
  mrrLabel,
  mrrOverrideLabel,
  mrrOverrideShort,
  mrrValue,
  netInvoiced,
  odooLegalName,
  outstandingLabel,
  reminderTierLabel,
  shortDate,
} from './customerLabels';
import type { CustomerList, CustomerRow, CustomerStage, PaymentStatus, UninvoicedOrder } from '../../../api/customers';

type Filter = 'all' | Extract<PaymentStatus, 'outstanding' | 'overdue'> | 'blocked';

const FILTER_LABEL: Record<Exclude<Filter, 'all'>, string> = {
  outstanding: 'Outstanding',
  overdue: 'Overdue',
  blocked: 'Blocked',
};
type StageFilter = CustomerStage | 'all';

/**
 * Active customers are the list; the rest are kept apart so a churned pilot
 * from 2024 never sits between two live contracts. Onboarding is second because
 * those are the ones somebody has to chase.
 */
const STAGES: ReadonlyArray<{ id: StageFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'onboarding', label: 'Onboarding' },
  { id: 'former', label: 'Former' },
  { id: 'unknown', label: 'Unknown' },
  { id: 'all', label: 'All' },
];

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * "3 invoices · 2 paid · 1 unsent · 2 scheduled" — the invoice ticks in one
 * line. An invoice dated after today is scheduled: not late to send, not owed.
 */
function invoicingSummary(customer: CustomerRow): string {
  if (customer.odooCompanyId === null) return UNKNOWN;
  const { created } = customer.invoicing;
  // "yet" only while the contract can still bill; never "none" when Odoo has
  // documents that were cancelled or never posted.
  if (created === 0) return noPostedInvoiceLabel(customer);
  const { paid, credited } = invoiceCounts(customer);
  const real = customer.invoicing.invoices.filter((i) => !i.isCreditNote);
  const scheduled = real.filter((i) => i.scheduled).length;
  const unsent = real.filter((i) => !i.scheduled && i.sent === false).length;
  const parts = [plural(created, 'invoice')];
  if (paid > 0) parts.push(`${paid} paid`);
  if (credited > 0) parts.push(`${credited} credited`);
  if (unsent > 0) parts.push(`${unsent} unsent`);
  if (scheduled > 0) parts.push(`${scheduled} scheduled`);
  return parts.join(' · ');
}

/**
 * A linked customer with no posted invoice. Odoo usually does hold documents
 * for them, all cancelled or still drafts from the 2024 migration (Kapsch: one
 * cancelled 2025 invoice, one 2024 draft), so "never invoiced" overstated it.
 */
function noPostedInvoiceLabel(customer: CustomerRow): string {
  const u = customer.invoicing.unposted;
  const base = customer.stage === 'former' ? 'no posted invoice in Odoo' : 'none posted yet';
  if (!u || u.cancelled + u.draft === 0) return base;
  const parts = [u.cancelled > 0 ? `${u.cancelled} cancelled` : null, u.draft > 0 ? `${plural(u.draft, 'draft')} never posted` : null];
  return `${base} · ${parts.filter(Boolean).join(', ')}`;
}

/** "1 cancelled, 1 draft never posted", or null when Odoo holds nothing for them (or counts are not sent). */
function unpostedDetail(customer: CustomerRow): string | null {
  const u = customer.invoicing.unposted;
  if (!u || u.cancelled + u.draft === 0) return null;
  return [u.cancelled > 0 ? `${u.cancelled} cancelled` : null, u.draft > 0 ? `${plural(u.draft, 'draft')} never posted` : null]
    .filter(Boolean)
    .join(', ');
}

/** The most recently issued real invoice, for the muted line under the summary. Scheduled ones are not issued yet. */
function latestInvoice(customer: CustomerRow) {
  return customer.invoicing.invoices
    .filter((i) => !i.isCreditNote && !i.scheduled)
    .reduce<CustomerRow['invoicing']['invoices'][number] | null>(
      (best, i) => (best === null || i.issueDate > best.issueDate ? i : best),
      null,
    );
}

/** The most recent reminder drafted for any of the customer's invoices. */
function latestReminder(customer: CustomerRow): { tier: string; at: string | null } | null {
  let best: { tier: string; at: string | null } | null = null;
  for (const invoice of customer.invoicing.invoices) {
    const r = invoice.reminder;
    if (!r) continue;
    if (best === null || (r.at ?? '') > (best.at ?? '')) best = r;
  }
  return best;
}

/** "S00210 · €45,000 to invoice", or the order alone when its amount is not synced. */
function toInvoiceLabel(order: UninvoicedOrder, count: number): string {
  const amount =
    order.invoiceAmount != null && order.sourceCurrency
      ? ` · ${money(order.invoiceAmount, order.sourceCurrency)} to invoice`
      : ' · to invoice';
  return `${order.orderName}${amount}${count > 1 ? ` (+${count - 1})` : ''}`;
}

/** "€5,959 / mo · ends 14 Sep 27" — what a not-live contract is worth and how long it runs. */
function contractLine(customer: CustomerRow): string | null {
  const sub = customer.subscription;
  if (!sub.phase) return null;
  const parts: string[] = [];
  if (sub.liveCount > 0) parts.push(`${mrrLabel(customer)} / mo`);
  // An ended contract says its end date on the line above, in place of a pill.
  if (sub.phase !== 'ended' && sub.expiresOn) parts.push(`ends ${shortDate(sub.expiresOn)}`);
  const billing = billingNoteShort(customer);
  if (billing && parts.length === 0) parts.push(billing);
  return parts.length ? parts.join(' · ') : null;
}

/** "ends 14 Sep 27 · Enterprise" — the grey line under a live contract's MRR. */
function liveContractSubLine(customer: CustomerRow): string | null {
  const sub = customer.subscription;
  const parts: string[] = [];
  const override = mrrOverrideShort(customer);
  if (override) parts.push(override);
  if (sub.expiresOn) parts.push(`ends ${shortDate(sub.expiresOn)}`);
  const billing = billingNoteShort(customer);
  if (billing) parts.push(billing);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The API's "Nothing to do" is the healthy default, so it reads as an empty
 * cell: only rows with something to chase should catch the eye.
 */
const isNothingToDo = (step: string | null) => step === null || /^nothing to do\.?$/i.test(step.trim());

/** A dash from the label helpers becomes an empty cell: one empty marker everywhere. */
const orBlank = (label: string) => (label === '—' ? null : label);

/**
 * "27 customers · €80,282 MRR · €12,400 outstanding" for the rows currently
 * listed. Former customers carry no MRR, so their footer says what they were
 * invoiced instead, and an amount nobody owes is left out rather than shown as €0.
 */
function footerSummary(rows: CustomerRow[], stage: StageFilter, endedIds: ReadonlySet<string> = new Set()): string {
  if (stage === 'former') {
    const invoiced = rows.reduce((sum, c) => (c.odooCompanyId === null ? sum : sum + netInvoiced(c.invoicing)), 0);
    const owed = rows.reduce((sum, c) => sum + Math.max(0, c.invoicing.outstanding), 0);
    return [
      plural(rows.length, 'customer'),
      `${money(invoiced, 'EUR')} invoiced over their contracts, net of credit notes`,
      ...(owed > 0 ? [`${money(owed, 'EUR')} still owed`] : []),
    ].join(' · ');
  }
  let mrr = 0;
  let incomplete = false;
  let outstanding = 0;
  let outstandingIncomplete = false;
  for (const c of rows) {
    if (c.odooCompanyId !== null) {
      outstanding += Math.max(0, c.invoicing.outstanding);
      if (c.invoicing.outstandingIncomplete) outstandingIncomplete = true;
    }
    if (c.subscription.liveCount > 0 && c.subscription.mrr === null && c.subscription.reportedMrr === null) {
      incomplete = true;
      continue;
    }
    mrr += mrrValue(c);
  }
  // Active rows plus whoever ended this month and still counts in its MRR: say which, so the
  // footer's count is the tile's 27 plus the named one, never an unexplained 28.
  const ending = rows.filter((c) => endedIds.has(c.id));
  const count =
    stage === 'active' && ending.length > 0
      ? `${rows.length - ending.length} active + ${ending.map((c) => c.companyName).join(', ')} (ended ${shortDate(ending[0].subscription.expiresOn)})`
      : plural(rows.length, 'customer');
  return [
    count,
    `${money(mrr, 'EUR')}${incomplete ? '+' : ''} MRR`,
    `${money(outstanding, 'EUR')}${outstandingIncomplete ? '+' : ''} outstanding`,
  ].join(' · ');
}

/**
 * Customer status: every closed-won customer and where it stands on contract,
 * invoicing and payment. One row per customer — renewals are extra deals on
 * the same row, not extra customers — and a customer the OS cannot match to an
 * Odoo partner says so rather than reporting zeroes. Each row opens the
 * customer's page, where the invoices, mails and reminder drafts live.
 */
/** Owes money now: the rows the Outstanding amount adds up, overdue ones included. */
function owesMoney(c: CustomerRow): boolean {
  return c.invoicing.outstanding > 0;
}

type Group = 'overdue' | 'todo' | 'waiting' | 'ended' | 'healthy' | 'unlinked' | 'former' | 'formerUnbilled';

const GROUPS: ReadonlyArray<{ id: Group; label: string }> = [
  { id: 'overdue', label: 'Overdue' },
  { id: 'todo', label: 'To do' },
  { id: 'waiting', label: 'Waiting' },
  { id: 'ended', label: 'Ended this month, still in MRR' },
  { id: 'healthy', label: 'Healthy' },
  // No Odoo partner, so no contract or invoice to judge: their own group, never counted as "To do".
  { id: 'unlinked', label: 'Not linked to Odoo' },
  { id: 'former', label: 'Former' },
  // 23 of 26 former rows said "no posted invoice in Odoo"; the heading says it once.
  { id: 'formerUnbilled', label: 'Former, no posted invoice in Odoo' },
];

/** "Wait: …" and "Scheduled: …" are the calendar's business, not Walter's. */
const isWaitStep = (step: string | null) => step !== null && /^(wait|scheduled):/i.test(step.trim());

/**
 * Exceptions first. Overdue: money past its due date, most days first. To do:
 * something to act on now (blocked in the handover, a resend, a reply, an
 * invoice to create or send). Waiting: money or a dated step pending, nothing
 * late. Ended this month: a contract that ran out but still counts in this
 * month's MRR. Healthy: nothing to do. Former: every contract ended; they
 * are one group, never "To do", because nothing is left to act on (a former
 * customer's blockedOn says only that no subscription is live).
 */
function groupOf(c: CustomerRow, endedIds: ReadonlySet<string>): Group {
  if (endedIds.has(c.id)) return 'ended';
  if (c.stage === 'unknown') return 'unlinked';
  if (c.stage === 'former') {
    if (c.paymentStatus === 'overdue') return 'overdue';
    return c.odooCompanyId !== null && c.invoicing.created === 0 ? 'formerUnbilled' : 'former';
  }
  if (c.paymentStatus === 'overdue') return 'overdue';
  // The register's next step is the one source: it already says "Resend …"
  // while a redirect is pending and "Wait: …" once the thread shows it was
  // sent, so the row's group and the detail page cannot disagree.
  const actionStep = !isNothingToDo(c.nextStep) && !isWaitStep(c.nextStep);
  if (actionStep || (c.blockedOn !== null && c.blockedOn !== 'payment')) {
    return 'todo';
  }
  if (owesMoney(c) || !isNothingToDo(c.nextStep)) return 'waiting';
  return 'healthy';
}

/** Within a group: most days overdue first, then the most money owed, then the name. */
function groupOrder(a: CustomerRow, b: CustomerRow): number {
  return (
    (b.invoicing.oldestOverdueDays ?? 0) - (a.invoicing.oldestOverdueDays ?? 0) ||
    Math.max(0, b.invoicing.outstanding) - Math.max(0, a.invoicing.outstanding) ||
    a.companyName.localeCompare(b.companyName)
  );
}

/** A group longer than this shows its first rows and a toggle, so paging never splits a group. */
const GROUP_COLLAPSE_AFTER = 20;
const GROUP_PREVIEW = 15;

/** "4 customers · €62,300 outstanding · €9,400 MRR" for one group, over all its rows. */
function groupSummary(rows: CustomerRow[], group: Group): string {
  if (group === 'unlinked') return `${plural(rows.length, 'customer')} · link an Odoo partner to see their status`;
  if (group === 'formerUnbilled') {
    return `${plural(rows.length, 'customer')} · linked to Odoo, where their documents are cancelled or drafts never posted`;
  }
  if (group === 'former') {
    const invoiced = rows.reduce((sum, c) => (c.odooCompanyId === null ? sum : sum + netInvoiced(c.invoicing)), 0);
    return [plural(rows.length, 'customer'), ...(invoiced > 0 ? [`${money(invoiced, 'EUR')} invoiced, net`] : [])].join(' · ');
  }
  const outstanding = rows.reduce((sum, c) => sum + Math.max(0, c.invoicing.outstanding), 0);
  const mrr = rows.reduce((sum, c) => sum + mrrValue(c), 0);
  const parts = [plural(rows.length, 'customer')];
  if (outstanding > 0) parts.push(`${money(outstanding, 'EUR')} outstanding`);
  if (mrr > 0) parts.push(`${money(mrr, 'EUR')} MRR`);
  return parts.join(' · ');
}

export function CustomersTab() {
  const { data, isLoading, error, refetch } = useCustomers();
  const navigate = useNavigate();
  // The view lives in the URL (?view=former), so a link or a reload keeps it.
  // An unknown value is said, not silently swapped for another view.
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const validView = viewParam === null || STAGES.some((item) => item.id === viewParam);
  const stage: StageFilter = viewParam !== null && validView ? (viewParam as StageFilter) : 'active';
  const setStage = (next: StageFilter) =>
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 'active') params.delete('view');
        else params.set('view', next);
        return params;
      },
      { replace: true },
    );
  const [filter, setFilter] = useState<Filter>('all');

  const customers = data?.customers ?? [];
  // Rows whose contract ended this month but still count in this month's MRR
  // (nextbike by TIER, ended 1 Oct). They sit under Active so the row set adds
  // up to the MRR tile: 27 active + 1 ended = the 28 customers the Overview counts.
  const mrrSummary = data?.summary.mrr;
  const endedIds = useMemo(
    () =>
      new Set(
        mrrSummary && mrrSummary.error === null
          ? mrrSummary.others.map((o) => o.customerId).filter((id): id is string => id !== null)
          : [],
      ),
    [mrrSummary],
  );
  const inStage = useMemo(
    () =>
      stage === 'all'
        ? customers
        : customers.filter((c) => c.stage === stage || (stage === 'active' && endedIds.has(c.id))),
    [customers, stage, endedIds],
  );
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<Group>>(new Set());
  const filtered = useMemo(() => {
    if (filter === 'all') return inStage;
    if (filter === 'blocked') return inStage.filter((c) => c.blockedOn !== null);
    // "Outstanding" is everyone who owes money, overdue included, the same rows its amount sums.
    if (filter === 'outstanding') return inStage.filter(owesMoney);
    return inStage.filter((c) => c.paymentStatus === filter);
  }, [inStage, filter]);

  // Exceptions first: Overdue (most days first), To do, Waiting, Ended, Healthy.
  // Every group is shown whole on one page; a long one folds behind a toggle,
  // so a page break never cuts a group in two.
  const { visible, byGroup } = useMemo(() => {
    const grouped = new Map<Group, CustomerRow[]>(GROUPS.map((g) => [g.id, []]));
    for (const c of filtered) grouped.get(groupOf(c, endedIds))!.push(c);
    for (const rows of grouped.values()) rows.sort(groupOrder);
    return { visible: GROUPS.flatMap((g) => grouped.get(g.id)!), byGroup: grouped };
  }, [filtered, endedIds]);

  if (error) {
    return <ErrorState title="Couldn’t load customers" error={error} onRetry={() => refetch()} />;
  }
  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        {/* Holds the view switch's height so the tiles do not jump when it arrives. */}
        <div className="h-8" aria-hidden />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {['Active customers', 'MRR', 'Outstanding', 'Overdue'].map((label) => (
            <TileSkeleton key={label} label={label} />
          ))}
        </div>
        <LoadingState rows={6} />
      </div>
    );
  }

  const s = data.summary;
  const linkage = data.linkage;
  const toggle = (next: Filter) => setFilter((prev) => (prev === next ? 'all' : next));
  const stageCount = (id: StageFilter) => (id === 'all' ? s.total : s.byStage[id]);
  const stageLabel = STAGES.find((item) => item.id === stage)?.label ?? 'All';
  const floor = s.outstandingIncomplete ? '+' : '';

  // Unlinked customers have no contract, invoice or payment to report: the view
  // is one sentence and a short list of who to link, not the company-wide tiles.
  if (stage === 'unknown') {
    const unknown = inStage.filter((c) => c.stage === 'unknown').sort((a, b) => a.companyName.localeCompare(b.companyName));
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <SegmentedControl
            ariaLabel="Customer stage"
            options={STAGES.filter((item) => item.id === 'all' || item.id === stage || stageCount(item.id) > 0).map(
              (item) => ({ id: item.id, label: `${item.label} ${stageCount(item.id)}` }),
            )}
            value={stage}
            onChange={(id) => {
              setStage(id);
              setFilter('all');
            }}
          />
        </div>
        <Alert variant="warning">
          {plural(unknown.length, 'customer')} {unknown.length === 1 ? 'is' : 'are'} not linked to an Odoo partner, so
          contract, invoices and payments are unknown until {unknown.length === 1 ? 'it is' : 'they are'} linked. Open one
          and choose its Odoo partner.
        </Alert>
        <Card padding="md">
          <CardHeader title="Unknown customers" />
          {unknown.length === 0 ? (
            <EmptyState compact title="No unlinked customers" />
          ) : (
            <Table minWidth={560} colWidths={['44%', '40%', '16%']}>
              <THead>
                <tr>
                  <Th>Customer</Th>
                  <Th>Closed won</Th>
                  <Th />
                </tr>
              </THead>
              <tbody>
                {unknown.map((c) => (
                  <Tr key={c.id} onClick={() => navigate(`/finance/customers/${c.id}`)} className="group">
                    <Td className="py-3 font-medium text-text-primary">{c.companyName}</Td>
                    <Td className="py-3 text-body-xs text-text-tertiary break-words">
                      {customerSinceLabel(c)}
                      {c.deals.length > 1 && ` · ${plural(c.deals.length, 'deal')}`}
                    </Td>
                    <Td align="right" className="py-3">
                      <span className="inline-flex items-center gap-2">
                        <Link
                          to={`/finance/customers/${c.id}`}
                          className="text-body-xs font-medium text-text-primary hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Link Odoo partner
                        </Link>
                        <a
                          href={c.attioUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open the deal in Attio"
                          aria-label={`Open ${c.companyName} in Attio`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-text-muted hover:bg-surface-tertiary hover:text-text-primary"
                        >
                          <ArrowUpRight className="h-4 w-4" aria-hidden />
                        </a>
                      </span>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    );
  }

  // Former customers owe nothing and have nothing to do: their view drops the
  // receivables, the blocked filter and any column empty on every row, and
  // shows what each was invoiced instead (what the tile adds up).
  const former = stage === 'former';
  const showOutstanding = !former || visible.some((c) => c.invoicing.outstanding > 0);
  const showNextStep = !former || visible.some((c) => !isNothingToDo(c.nextStep));
  // Customer, Contract, Invoicing and the link column, plus the optional ones.
  const columnCount = 4 + Number(former) + Number(showOutstanding) + Number(showNextStep);
  const colWidths = [
    '22%',
    '18%',
    '24%',
    ...(former ? ['12%'] : []),
    ...(showOutstanding ? ['13%'] : []),
    ...(showNextStep ? ['24%'] : []),
    '4.5rem',
  ];

  return (
    <div className="space-y-6">
      {!validView && (
        <Alert variant="warning">
          There is no “{viewParam}” view of the customers; showing Active. The views are Active, Onboarding, Former,
          Unknown and All.
        </Alert>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SegmentedControl
          ariaLabel="Customer stage"
          // A stage nobody is in is not a choice; the current one always stays.
          options={STAGES.filter((item) => item.id === 'all' || item.id === stage || stageCount(item.id) > 0).map(
            (item) => ({ id: item.id, label: `${item.label} ${stageCount(item.id)}` }),
          )}
          value={stage}
          onChange={(id) => {
            setStage(id);
            setFilter('all');
          }}
        />
        {/* On All the tile names them and on Unknown they are the list: the nudge is for the other views. */}
        {linkage.unlinked > 0 && stage !== 'all' && (
          <p className="flex items-center gap-1.5 text-body-xs text-text-tertiary">
            <AlertCircle className="h-3.5 w-3.5 text-warning" aria-hidden />
            {linkage.unlinked} customers aren&apos;t linked to Odoo, so their status is unknown.
            <button
              type="button"
              className="font-medium text-text-secondary hover:text-text-primary hover:underline"
              onClick={() => {
                setStage('unknown');
                setFilter('all');
              }}
            >
              Show them
            </button>
          </p>
        )}
      </div>

      {stage === 'former' ? (
        <FormerTiles rows={inStage} total={s.total} endedIds={endedIds} />
      ) : (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile
          label={stage === 'all' ? 'Customers' : `${stageLabel} customers`}
          value={String(inStage.filter((c) => stage === 'all' || c.stage === stage).length)}
          sub={
            stage === 'all'
              ? STAGES.filter((item) => item.id !== 'all' && stageCount(item.id) > 0)
                  .map((item) => `${stageCount(item.id)} ${item.id === 'unknown' ? 'not linked' : item.label.toLowerCase()}`)
                  .join(' · ') + ', closed won'
              : `of ${s.total} closed won${
                  stage === 'active' && endedIds.size > 0 ? ` · + ${endedIds.size} ended this month, still in MRR` : ''
                }`
          }
        />
        <MrrTile summary={s} />
        <StatTile
          label="Outstanding"
          value={`${money(s.outstandingAmount, s.currency)}${floor}`}
          sub={`${customers.filter(owesMoney).length} customers${
            s.scheduledAmount > 0 ? ` · ${money(s.scheduledAmount, s.currency)} scheduled` : ''
          }`}
          tooltip="Open on invoices dated today or earlier. Invoices dated after today are scheduled: booked in Odoo, not owed yet, so not counted here."
          onClick={() => toggle('outstanding')}
        />
        <StatTile
          label="Overdue"
          value={`${money(s.overdueAmount, s.currency)}${floor}`}
          sub={`${s.overdue} customers`}
          onClick={() => toggle('overdue')}
        />
      </div>
      )}

      {!former && <ReceivablesAging />}

      <Card padding="md">
        <CardHeader
          title={`${stageLabel} customers`}
          action={
            <div className="flex items-center gap-2">
              {filter !== 'all' && (
                <button
                  type="button"
                  onClick={() => setFilter('all')}
                  className="inline-flex items-center gap-1 rounded-sm bg-surface-tertiary px-2 py-0.5 text-body-xs font-medium text-text-secondary hover:text-text-primary"
                >
                  {FILTER_LABEL[filter]}
                  <X className="h-3 w-3" aria-label="Clear filter" />
                </button>
              )}
              {/* Same size as the stage switch and filter chip beside it, not a full button. */}
              {!former && <button
                type="button"
                onClick={() => toggle('blocked')}
                className="rounded-sm px-3 py-1 text-body-xs font-medium text-text-secondary transition-colors hover:bg-surface-tertiary hover:text-text-primary"
              >
                {filter === 'blocked' ? 'Show all' : 'Show blocked only'}
              </button>}
            </div>
          }
        />

        {visible.length === 0 ? (
          <EmptyState compact title={`No ${stageLabel.toLowerCase()} customers match this filter`} />
        ) : (
          <>
          {/* Outstanding is right-aligned and Next step left-aligned, so the
              Outstanding column carries extra right padding as the gap between them. */}
          <Table minWidth={960} colWidths={colWidths}>
            <THead>
              <tr>
                <Th>Customer</Th>
                <Th>Contract</Th>
                <Th>Invoicing</Th>
                {former && <Th align="right">Invoiced, net</Th>}
                {showOutstanding && <Th align="right" className="!pr-8">Outstanding</Th>}
                {showNextStep && <Th>Next step</Th>}
                <Th />
              </tr>
            </THead>
            <tbody>
              {GROUPS.map((g) => {
                const rows = byGroup.get(g.id) ?? [];
                if (rows.length === 0) return null;
                const folds = rows.length > GROUP_COLLAPSE_AFTER;
                const open = !folds || expandedGroups.has(g.id);
                const shown = open ? rows : rows.slice(0, GROUP_PREVIEW);
                return (
                  <Fragment key={g.id}>
                    {/* When Former is the only group, the card is the group; its footer says the same. */}
                    {!(g.id === 'former' && GROUPS.every((o) => o.id === 'former' || !byGroup.get(o.id)?.length)) && (
                      <TGroupRow colSpan={columnCount} label={g.label} summary={groupSummary(rows, g.id)} />
                    )}
                    {shown.map((c) => renderRow(c, g.id))}
                    {folds && (
                      <tr>
                        <td colSpan={columnCount} className="px-4 py-2">
                          <button
                            type="button"
                            className="text-body-xs font-medium text-text-tertiary hover:text-text-primary"
                            onClick={() =>
                              setExpandedGroups((prev) => {
                                const next = new Set(prev);
                                if (next.has(g.id)) next.delete(g.id);
                                else next.add(g.id);
                                return next;
                              })
                            }
                          >
                            {open ? 'Show fewer' : `Show all ${rows.length} in ${g.label}`}
                          </button>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </Table>
          <TableFooter>{footerSummary(visible, stage, endedIds)}</TableFooter>
          </>
        )}
      </Card>
    </div>
  );

  function renderRow(c: CustomerRow, group: Group) {
                const sub = c.subscription;
                const line = sub.phase === 'live' ? liveContractSubLine(c) : contractLine(c);
                const last = latestInvoice(c);
                const reminder = latestReminder(c);
                const insight = latestInsight(c);
                const insightTag = insight ? insightBadge(insight.kind) : null;
                // One status pill at most under Outstanding: Overdue wins, then what
                // the customer said. Both are about money already invoiced. An order
                // not invoiced yet is not part of that amount, so its pill sits in
                // the Invoicing column with the order's own amount instead.
                const overdue = c.paymentStatus === 'overdue';
                const statusPill = overdue
                  ? {
                      variant: PAYMENT_BADGE.overdue.variant,
                      label: `${PAYMENT_BADGE.overdue.label}${c.invoicing.oldestOverdueDays ? ` · ${c.invoicing.oldestOverdueDays}d` : ''}`,
                    }
                  : insightTag;
                const toInvoice = c.uninvoicedOrders[0] ?? null;
                const nextStep = isNothingToDo(c.nextStep) ? null : c.nextStep;
                const href = `/finance/customers/${c.id}`;
                return (
                  <Tr key={c.id} onClick={() => navigate(href)} className="group align-top">
                    <Td className="py-3">
                      <div className="flex items-start gap-2 min-w-0">
                        <Link
                          to={href}
                          className="min-w-0 break-words font-medium text-text-primary hover:underline"
                          title={c.companyName}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {c.companyName}
                        </Link>
                        {/* Under the Former heading a "Former" pill on each row would only repeat it. */}
                        {/* Active is the normal case: only the exception (Unknown, Former outside its group) earns a pill. */}
                        {/* Only a former customer outside the Former groups (ended this month, or overdue) needs the pill;
                            an Unknown row says so in its own cell. */}
                        {stage === 'all' && c.stage === 'former' && group !== 'former' && group !== 'formerUnbilled' && (
                          <Badge variant={STAGE_BADGE[c.stage].variant}>{STAGE_BADGE[c.stage].label}</Badge>
                        )}
                      </div>
                      {odooLegalName(c) && (
                        <p className="mt-0.5 text-body-xs text-text-tertiary break-words" title={`Odoo: ${odooLegalName(c)}`}>
                          {odooLegalName(c)}
                        </p>
                      )}
                      {/* Wraps rather than truncates: "won 6 Aug 2026 · from 5 Oct 2026" is the point of the line. */}
                      <p className="mt-0.5 text-body-xs text-text-tertiary break-words" title={c.dealName ?? undefined}>
                        {customerSinceLabel(c)}
                        {c.deals.length > 1 && ` · ${plural(c.deals.length, 'deal')}`}
                      </p>
                    </Td>
                    {c.odooCompanyId === null ? (
                      // Nothing can be said about an unlinked customer: one cell that says what to do,
                      // not three "unknown"s and a step.
                      <Td className="py-3" colSpan={columnCount - 2}>{/* everything between the name and the links */}
                        <p className="text-text-secondary">
                          Not linked to an Odoo partner.{' '}
                          <Link
                            to={href}
                            className="font-medium text-text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Link Odoo partner
                          </Link>
                        </p>
                      </Td>
                    ) : (
                      <>
                    <Td className="py-3">
                      {/* A live contract is the normal case, so it shows its value, not a "Live" pill. */}
                      {sub.phase === 'live' ? (
                        <p className="tabular-nums text-text-primary whitespace-nowrap">
                          {orBlank(mrrLabel(c))}
                          {sub.liveCount > 0 && <span className="text-text-tertiary"> / mo</span>}
                        </p>
                      ) : sub.phase === 'ended' ? (
                        // Every former row has ended, so a red pill on each said nothing: the date is the news.
                        <>
                          {endedStillInMrr(c) && (
                            <p className="tabular-nums text-text-primary whitespace-nowrap">
                              {money(sub.reportedMrr ?? 0, 'EUR')}
                              <span className="text-text-tertiary"> / mo</span>
                            </p>
                          )}
                          <p className="tabular-nums text-text-secondary whitespace-nowrap">
                            {sub.expiresOn ? `Ended ${shortDate(sub.expiresOn)}` : 'Ended'}
                          </p>
                        </>
                      ) : sub.phase ? (
                        <Badge variant={PHASE_VARIANT[sub.phase]}>{contractBadgeLabel(sub.phase, sub.startsOn)}</Badge>
                      ) : c.odooCompanyId === null ? (
                        <span className="text-text-muted">{UNKNOWN}</span>
                      ) : null}
                      {/* In full: an override's reason (Swiss GRC, ISS NA) is the point of the line. */}
                      {line && (
                        <p
                          className="mt-1 text-body-xs text-text-tertiary break-words"
                          title={[mrrOverrideLabel(c), billingNote(c)].filter(Boolean).join('\n') || undefined}
                        >
                          {line}
                        </p>
                      )}
                      {/* The sheet and the invoice disagree for the term: an exception, so it shows. */}
                      {sub.mrrOverride?.invoiceConflict && (
                        <p className="mt-1" title={overrideConflictSentence(sub.mrrOverride.invoiceConflict)}>
                          <Badge variant="warning">Override ≠ invoice</Badge>
                        </p>
                      )}
                    </Td>
                    <Td className="py-3">
                      {/* Under "Former, no posted invoice in Odoo" the heading says it; the cell keeps only the counts. */}
                      {group === 'formerUnbilled' ? (
                        unpostedDetail(c) && <p className="text-body-xs text-text-tertiary">{unpostedDetail(c)}</p>
                      ) : (
                        <p
                          className={'break-words ' + (c.invoicing.created > 0 ? 'text-text-primary' : 'text-text-muted')}
                          title={invoicingSummary(c)}
                        >
                          {invoicingSummary(c)}
                        </p>
                      )}
                      {last && (
                        <p
                          className="mt-0.5 text-body-xs text-text-tertiary break-words tabular-nums"
                          title={`${last.invoiceNumber} · ${shortDate(last.issueDate)}`}
                        >
                          {last.invoiceNumber} · {shortDate(last.issueDate)}
                        </p>
                      )}
                      {toInvoice && (
                        <p
                          className="mt-1"
                          title={c.uninvoicedOrders
                            .map((o) => `${o.orderName}: due ${shortDate(o.nextInvoiceOn)}, nothing invoiced in Odoo`)
                            .join('\n')}
                        >
                          <Badge variant="warning">{toInvoiceLabel(toInvoice, c.uninvoicedOrders.length)}</Badge>
                        </p>
                      )}
                    </Td>
                    {former && (
                      <Td align="right" className="py-3 tabular-nums whitespace-nowrap">
                        {c.invoicing.created > 0 ? (
                          money(netInvoiced(c.invoicing), 'EUR')
                        ) : null}
                      </Td>
                    )}
                    {showOutstanding && (
                    <Td align="right" className="py-3 !pr-8">
                      <p
                        className={
                          'whitespace-nowrap tabular-nums ' +
                          (c.invoicing.outstanding > 0 ? 'font-medium text-text-primary' : 'text-text-muted')
                        }
                      >
                        {/* Nothing owed is a blank cell, not a dot: healthy rows stay quiet. */}
                        {outstandingLabel(c) === '—' ? null : outstandingLabel(c)}
                      </p>
                      {statusPill && (
                        <p className="mt-1" title={overdue ? undefined : insight?.summary}>
                          <Badge variant={statusPill.variant}>{statusPill.label}</Badge>
                        </p>
                      )}
                    </Td>
                    )}
                    {showNextStep && (
                    <Td className="py-3">
                      {nextStep ? (
                        // In full: the step is the point of the row.
                        <p
                          className="break-words text-text-primary"
                          title={c.blockedOn ? `${nextStep} (${BLOCKED_LABEL[c.blockedOn]})` : nextStep}
                        >
                          {nextStep}
                        </p>
                      ) : null}
                      {(c.lastChased || reminder) && (
                        <p className="mt-0.5 text-body-xs text-text-tertiary break-words" title={[
                          c.lastChased ? `Chased ${dayMonth(c.lastChased.at)}` : null,
                          reminder ? `Reminder drafted${reminder.at ? ` ${dayMonth(reminder.at)}` : ''}: ${reminderTierLabel(reminder.tier)}` : null,
                        ].filter(Boolean).join(' · ')}>
                          {c.lastChased && `Chased ${dayMonth(c.lastChased.at)}`}
                          {c.lastChased && reminder && ' · '}
                          {reminder &&
                            `Reminder drafted${reminder.at ? ` ${dayMonth(reminder.at)}` : ''}: ${reminderTierLabel(
                              reminder.tier,
                            )}`}
                        </p>
                      )}
                    </Td>                    )}
                      </>
                    )}
                    <Td align="right" className="py-3">
                      <span className="inline-flex items-center gap-1">
                        <a
                          href={c.attioUrl}
                          target="_blank"
                          rel="noreferrer"
                          title="Open the deal in Attio"
                          aria-label={`Open ${c.companyName} in Attio`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-text-muted hover:bg-surface-tertiary hover:text-text-primary"
                        >
                          <ArrowUpRight className="h-4 w-4" aria-hidden />
                        </a>
                        <span className="inline-flex text-text-muted group-hover:text-text-primary transition-colors">
                          <ChevronRight className="h-4 w-4" aria-hidden />
                        </span>
                      </span>
                    </Td>
                  </Tr>
                );
  }
}

/**
 * The Overview's MRR, said once more here and bridged to the list: the Active
 * customers' figures, plus whatever this month counts outside that list (a
 * contract that ends on the 1st still counts for its month).
 */
function MrrTile({ summary }: { summary: CustomerList['summary'] }) {
  const m = summary.mrr;
  if (m.error !== null) {
    return <StatTile label="MRR" value="Unavailable" sub="The MRR figures could not be computed" tooltip={m.error} />;
  }
  const others = m.others.map((o) => `${o.name} ${money(o.mrr, summary.currency)}`).join(' + ');
  return (
    <StatTile
      label="MRR"
      value={money(m.total, summary.currency)}
      sub={
        m.others.length > 0
          ? `${money(m.activeTotal, summary.currency)} from ${m.activeCustomers} active + ${others}`
          : `This month, all from the ${m.activeCustomers} active customers`
      }
      tooltip={[
        'The same MRR as the Overview: counted Odoo contracts with setup fees spread and the MRR sheet’s overrides applied.',
        ...m.others.map((o) => `${o.name} (${money(o.mrr, summary.currency)}): ${o.why}.`),
      ].join(' ')}
    />
  );
}

/** "linked partners hold 14 cancelled and 31 never-posted documents", or the plain fact when counts are not sent. */
function unpostedSub(rows: CustomerRow[]): string {
  if (rows.some((c) => c.invoicing.unposted === undefined)) return 'linked to Odoo, nothing posted';
  const cancelled = rows.reduce((sum, c) => sum + (c.invoicing.unposted?.cancelled ?? 0), 0);
  const draft = rows.reduce((sum, c) => sum + (c.invoicing.unposted?.draft ?? 0), 0);
  if (cancelled + draft === 0) return 'linked to Odoo, nothing posted';
  return `their Odoo documents: ${cancelled} cancelled, ${draft} drafts never posted`;
}

/**
 * The Former view's own tiles: MRR and outstanding are the active book's
 * numbers and read as zero here. What matters for customers whose contracts
 * all ended is how many, how recently, what they were billed, and anything
 * still owed.
 */
function FormerTiles({ rows, total, endedIds }: { rows: CustomerRow[]; total: number; endedIds: ReadonlySet<string> }) {
  const former = rows.filter((c) => c.stage === 'former');
  const yearAgo = new Date();
  yearAgo.setFullYear(yearAgo.getFullYear() - 1);
  const yearAgoIso = yearAgo.toISOString().slice(0, 10);
  const ended = former
    .filter((c) => c.subscription.phase === 'ended' && c.subscription.expiresOn)
    .sort((a, b) => b.subscription.expiresOn!.localeCompare(a.subscription.expiresOn!));
  const recent = ended.filter((c) => c.subscription.expiresOn!.slice(0, 10) >= yearAgoIso);
  const latest = ended[0] ?? null;
  const billed = former.filter((c) => c.odooCompanyId !== null && c.invoicing.created > 0);
  const invoiced = billed.reduce((sum, c) => sum + netInvoiced(c.invoicing), 0);
  const owed = former.reduce((sum, c) => sum + Math.max(0, c.invoicing.outstanding), 0);
  // The same rows as the "Former, no posted invoice in Odoo" group below: one
  // that ended this month sits in its own group above and is counted there.
  const unbilled = former.filter((c) => c.odooCompanyId !== null && c.invoicing.created === 0);
  const neverBilled = unbilled.filter((c) => !endedIds.has(c.id)).length;
  const endedUnbilled = unbilled.filter((c) => endedIds.has(c.id));
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
      <StatTile label="Former customers" value={String(former.length)} sub={`of ${total} closed won`} />
      <StatTile
        label="Ended in 12 months"
        value={String(recent.length)}
        sub={latest ? `latest: ${latest.companyName}, ${shortDate(latest.subscription.expiresOn)}` : undefined}
        tooltip="Former customers whose last Odoo contract ended in the past year."
      />
      <StatTile
        label="Invoiced, net"
        value={money(invoiced, 'EUR')}
        sub={`${plural(billed.length, 'customer')} invoiced in Odoo · net of credit notes`}
        tooltip="Every Odoo invoice on the former customers' linked partners, less credit notes, dated today or earlier."
      />
      {owed > 0 ? (
        <StatTile label="Still owed" value={money(owed, 'EUR')} sub="open on former customers' invoices" />
      ) : (
        <StatTile
          label="No posted invoice in Odoo"
          value={String(neverBilled)}
          sub={`${unpostedSub(unbilled.filter((c) => !endedIds.has(c.id)))}${
            endedUnbilled.length > 0 ? ` · + ${endedUnbilled.map((c) => c.companyName).join(', ')}, ended this month` : ''
          }`}
          tooltip="Linked to an Odoo partner, but no invoice on it was ever posted. Odoo usually holds documents for them that were cancelled, or drafts from the 2024 migration never posted, so their billing is not in Odoo's books. The partner links themselves are right."
        />
      )}
    </div>
  );
}
